import {TKey} from '@/translations/i18n'
import {AccessesType, Access, AccessesColumn} from './types'
import {CreateAbsenceFormValidationSchema, CreateAccessFormValidationSchema} from './formSchemas'
import {accessTypeKeyNameToDefaultDuration} from '../agenda/utils'
import {HttpCreateAccessOptions} from './http/accesses.http'
import dayjs, {formatDate} from '@/dayjs'
import {calculateAccessRateCode} from '@/utilities/utils'

export const TIMEOUT_EXPORT_ACCESSES = 600000

export const communicatedAttachmentAcceptedFileTypes = {
    'application/pdf': ['.pdf'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png']
}

export const accessTypeToLabel = {
    absence: 'commons:absence',
    access: 'commons:access'
} as const satisfies Record<AccessesType, TKey>

/**
 * Column → label map for the accesses table and the export history summary (shared by the
 * `accesses_raw`, `accesses_report` and `accesses_ria` export entities).
 */
export const accessesColumnToLabel = {
    [AccessesColumn.enum.date]: 'commons:date',
    [AccessesColumn.enum.type]: 'commons:type',
    [AccessesColumn.enum.sector]: 'accesses:mode',
    [AccessesColumn.enum.assistiveRegime]: 'commons:care_sub_regime',
    [AccessesColumn.enum.groupName]: 'accesses:group',
    [AccessesColumn.enum.accessType]: 'accesses:access_type',
    [AccessesColumn.enum.consultantId]: 'commons:operator',
    [AccessesColumn.enum.status]: 'accesses:validation',
    [AccessesColumn.enum.report]: 'accesses:report',
    [AccessesColumn.enum.aslId]: 'commons:asl',
    [AccessesColumn.enum.rateCode]: 'accesses:rate',
    [AccessesColumn.enum.remappedSiarAccessType]: 'accesses:siar_accesses',
    [AccessesColumn.enum.access_100]: 'accesses:accesses_100',
    [AccessesColumn.enum.isRemote]: 'accesses:remote',
    [AccessesColumn.enum.absence_20]: 'accesses:absence_20',
    [AccessesColumn.enum.absence_50]: 'accesses:absence_50',
    [AccessesColumn.enum.absence_nr]: 'accesses:absence_nr',
    [AccessesColumn.enum.communicatedOnTime]: 'accesses:absence_type',
    [AccessesColumn.enum.patientId]: 'commons:patient',
    [AccessesColumn.enum.projectCode]: 'commons:pri_n',
    [AccessesColumn.enum.projectStartDate]: 'accesses:pri_start_date',
    [AccessesColumn.enum.projectExpirationDate]: 'accesses:pri_expiration_date',
    [AccessesColumn.enum.projectStatus]: 'accesses:pri_status'
} as const satisfies Record<Exclude<AccessesColumn, 'select' | 'moreActions'>, TKey | null>

export const accessesStatusToLabel = {
    validated: 'accesses:status_validated',
    to_validate: 'accesses:status_to_validate',
    excluded: 'accesses:status_excluded',
    accounted: 'accesses:status_accounted',
    discarded: 'accesses:status_discarded'
} as const satisfies Record<Access['status'], TKey>

const ACCESSES_RIA_ERROR_LABELS: Record<string, TKey> = {
    'accesses-with-wrong-status': 'accesses:export_ria_blocked_message'
}

export const resolveExportRiaErrorLabel = (code: string | undefined): TKey =>
    (code ? ACCESSES_RIA_ERROR_LABELS[code] : undefined) ?? 'errors:default'

export const accessFormValuesToPayload = (
    values: CreateAccessFormValidationSchema
): HttpCreateAccessOptions['payload'] => {
    const duration = accessTypeKeyNameToDefaultDuration[values.accessType.accessType]
    const rateCode = calculateAccessRateCode({
        sector: values.project.sector,
        serviceType: values.project.patient.serviceType,
        assistiveRegime: values.project.assistiveRegime || undefined
    })
    if (values.accessMode === 'single_day') {
        return [
            {
                patientId: values.patient.id,
                projectId: values.project.id,
                consultantId: values.consultant.id,
                accessTypeId: values.accessType.id,
                type: 'access',
                rateCode: rateCode || null,
                isARecovery: values.isARecovery,
                isRemote: values.isRemote,
                sector: values.sector,
                date: formatDate(values.date),
                time: values.time,
                duration: duration,
                missedAccessId: values.missedAccess?.id ?? null,
                attachmentId: null
            }
        ]
    }
    const accessToCreate = Array.from({length: values.numberOfAccesses ?? 1}, () => ({
        patientId: values.patient.id,
        projectId: values.project.id,
        consultantId: values.consultant.id,
        accessTypeId: values.accessType.id,
        type: 'access',
        rateCode: rateCode || null,
        sector: values.sector,
        date: formatDate(values.date),
        duration: duration
    }))
    return accessToCreate
}

export const absenceFormValuesToPayload = (
    values: CreateAbsenceFormValidationSchema
): HttpCreateAccessOptions['payload'] => {
    const duration = accessTypeKeyNameToDefaultDuration[values.accessType.accessType]
    return Array.from({length: values.numberOfAbsences ?? 1}, () => ({
        patientId: values.patient.id,
        projectId: values.project.id,
        consultantId: values.consultant.id,
        accessTypeId: values.accessType.id,
        type: 'absence',
        sector: values.sector,
        date: formatDate(values.date),
        duration: duration,
        rateCode: null,
        communicatedOnTime: values.communicatedOnTime ? values.communicatedOnTime === 'over_48_hours' : undefined,
        attachmentId: values.communicatedAttachment?.id ?? null
    }))
}

/**
 * Maps an Access object to form values for creating/editing accesses or absences.
 *
 * This function provides dynamic typing based on the access type:
 * - When `access.type === 'access'`: Returns `Partial<CreateAccessFormValidationSchema>`
 * - When `access.type === 'absence'`: Returns `Partial<CreateAbsenceFormValidationSchema>`
 *
 * @param access - The Access object to map to form values
 * @param missedAccess - Optional missed access (only used for recovery accesses)
 *
 * @returns Form values object with type-specific fields:
 *
 * **For Access (`access.type === 'access'`):**
 * - Common fields: patient, project, consultant, accessType, sector, date
 * - Access-specific: accessMode, time, isRemote, isARecovery, missedAccess
 *
 * **For Absence (`access.type === 'absence'`):**
 * - Common fields: patient, project, consultant, accessType, sector, date
 * - Absence-specific: communicatedOnTime, communicatedAttachment
 *
 */
// Function overloads for type safety
export function mapAccessToFormValues(
    access: Access & {type: 'access'},
    missedAccess?: Access
): Partial<CreateAccessFormValidationSchema>

export function mapAccessToFormValues(
    access: Access & {type: 'absence'},
    missedAccess?: Access
): Partial<CreateAbsenceFormValidationSchema>

export function mapAccessToFormValues(
    access: Access,
    missedAccess?: Access
): Partial<CreateAccessFormValidationSchema | CreateAbsenceFormValidationSchema>

export function mapAccessToFormValues(
    access: Access,
    missedAccess?: Access
): Partial<CreateAccessFormValidationSchema | CreateAbsenceFormValidationSchema> {
    // Common fields for both access and absence
    const patient =
        access.patientId && access.event.eventPatients?.[0]?.patient?.fullName
            ? {
                  id: access.patientId,
                  fullName: access.event.eventPatients?.[0]?.patient?.fullName
              }
            : undefined

    const project =
        access.projectId && access.project?.sector && access.patient?.serviceType
            ? {
                  id: access.projectId,
                  sector: access.project.sector,
                  patient: {serviceType: access.patient.serviceType},
                  code: access.project.code,
                  startDate: access.project.startDate,
                  expirationDate: access.project.expirationDate,
                  assistiveRegime: access.project.assistiveRegime
              }
            : undefined

    const consultant =
        access.consultantId && access.event.eventConsultants?.[0]?.user?.fullName
            ? {
                  id: access.consultantId,
                  fullName: access.event.eventConsultants?.[0]?.user?.fullName,
                  specializationId: access.event.eventConsultants?.[0]?.user?.specializationId,
                  specializationName: access.event.eventConsultants?.[0]?.user?.specialization
              }
            : undefined

    const accessType =
        access.event.eventConsultants?.[0]?.accessTypeId && access.event.eventConsultants?.[0]?.accessType?.accessType
            ? {
                  id: access.event.eventConsultants?.[0]?.accessTypeId,
                  accessType: access.event.eventConsultants?.[0]?.accessType?.accessType
              }
            : undefined

    const date = access.event.date ? new Date(access.event.date) : undefined

    // Base object with common fields
    const baseFormValues = {
        patient,
        project,
        consultant,
        accessType,
        sector: access.event.sector,
        date
    }

    // Type-specific logic
    if (access.type === 'access') {
        const time = access.event.time ? dayjs(access.event.time, 'HH:mm:ss').format('HH:mm') : undefined

        return {
            ...baseFormValues,
            accessMode: 'single_day',
            time,
            isRemote: access.event.isRemote || undefined,
            isARecovery: access.event.isARecovery || undefined,
            missedAccess
        } as Partial<CreateAccessFormValidationSchema>
    } else {
        // access.type === 'absence'
        return {
            ...baseFormValues,
            communicatedOnTime:
                access.event.communicatedOnTime != null
                    ? access.event.communicatedOnTime
                        ? 'over_48_hours'
                        : 'within_48_hours'
                    : undefined,
            communicatedAttachment: access.event.attachment
        } as Partial<CreateAbsenceFormValidationSchema>
    }
}
